import * as THREE from 'three';

/** Leon Paul–inspired electric foil kit materials (procedural PBR + optional photo refs). */

const texLoader = new THREE.TextureLoader();

export function makeLameMaterial(tint: number): THREE.MeshStandardMaterial {
  // Foil lamé: fine metallic weave (conductive cloth over white jacket)
  return new THREE.MeshStandardMaterial({
    color: tint,
    metalness: 0.92,
    roughness: 0.28,
    envMapIntensity: 1.35,
  });
}

export function makeJacketMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xf4f1ea,
    metalness: 0.05,
    roughness: 0.72,
  });
}

export function makeBreechesMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xefede6,
    metalness: 0.04,
    roughness: 0.78,
  });
}

export function makeMaskShellMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xc8ccd4,
    metalness: 0.95,
    roughness: 0.22,
  });
}

export function makeMaskBibMaterial(conductive: boolean): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: conductive ? 0xa8adb8 : 0x1a1c22,
    metalness: conductive ? 0.9 : 0.1,
    roughness: conductive ? 0.32 : 0.85,
  });
}

export function makeGloveMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xe8e4da,
    metalness: 0.08,
    roughness: 0.65,
  });
}

export function makeBladeMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xdde3ec,
    metalness: 1,
    roughness: 0.12,
    envMapIntensity: 1.5,
  });
}

export function makeGuardMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xb8bec8,
    metalness: 0.98,
    roughness: 0.3,
  });
}

export function makeGripMaterial(): THREE.MeshStandardMaterial {
  // Orthopaedic / pistol grip — dark polymer (Leon Paul style)
  return new THREE.MeshStandardMaterial({
    color: 0x1c1f28,
    metalness: 0.15,
    roughness: 0.55,
  });
}

export function makeSockMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xf7f5f0,
    metalness: 0.02,
    roughness: 0.9,
  });
}

export function makeShoeMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xf0f0f0,
    metalness: 0.2,
    roughness: 0.45,
  });
}

export function makePisteMetal(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x8a919c,
    metalness: 0.88,
    roughness: 0.35,
  });
}

export function makePisteWood(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x2a2118,
    metalness: 0.05,
    roughness: 0.85,
  });
}

export function makeFloorMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x0c1018,
    metalness: 0.2,
    roughness: 0.9,
  });
}

/** Canvas-based lame weave detail */
export function createLameTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#b8bcc6';
  ctx.fillRect(0, 0, size, size);
  for (let y = 0; y < size; y += 2) {
    for (let x = 0; x < size; x += 2) {
      const v = 160 + ((x * 13 + y * 7) % 40);
      ctx.fillStyle = `rgb(${v},${v + 2},${v + 6})`;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 0.5;
  for (let i = -size; i < size * 2; i += 4) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(4, 6);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function createMeshMaskTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#1a1c20';
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = '#c5cad3';
  ctx.lineWidth = 1.2;
  const step = 6;
  for (let y = 0; y <= size; y += step) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(size, y);
    ctx.stroke();
  }
  for (let x = 0; x <= size; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, size);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 3);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Optional photo-based albedo from Leon Paul product refs (fails silently). */
export function loadRefTexture(path: string, repeatX = 1, repeatY = 1): THREE.Texture | null {
  try {
    const tex = texLoader.load(path);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeatX, repeatY);
    return tex;
  } catch {
    return null;
  }
}

/** Blade edge highlight gradient for foil tip glow */
export function createTipGlowTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
  g.addColorStop(0, 'rgba(200,240,255,1)');
  g.addColorStop(0.4, 'rgba(120,200,255,0.5)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
